/**
 * LLM Model API Client
 * Handles all API calls for LLM Model entities
 */

import { BaseApiClient, ApiResponse } from './baseApiClient';
import type { StandardListRequest, GetByIdRequest } from '../types';

export interface LlmModel {
  LlmModelID: number;
  Name: string;
  ModelName?: string; // API may return ModelName instead of Name
  Description?: string;
  ModelCode: string;
  IsActive: boolean;
  CreatedOn?: string;
}

export class LlmModelApiClient extends BaseApiClient {
  /**
   * Get all active LLM models
   */
  async getActive(request: StandardListRequest = {}): Promise<ApiResponse<LlmModel[]>> {
    const requestWithPagination = {
      PageInfo: {
        PageNumber: 1,
        PageSize: 1000,
      },
      ...request,
    };
    return this.postQuery<LlmModel[]>('/api/v1/ai-llm/models/active', requestWithPagination);
  }

  /**
   * Get LLM model by ID
   */
  async getById(request: GetByIdRequest): Promise<ApiResponse<LlmModel>> {
    const webRequest = {
      ID: {
        ID: request.id,
      },
    };
    return this.postQuery<LlmModel>('/api/v1/ai-llm/models/get-by-id', webRequest);
  }

  /**
   * Create new LLM model
   */
  async create(model: any): Promise<ApiResponse<LlmModel>> {
    const response = await this.post<any>('/api/v1/ai-llm/models', model);

    // Handle response structure - API returns id in response.id.id or response.data.Id.id
    if (response.success) {
      let llmModelId = null;

      // Try to extract ID from various possible locations
      const r = response as any;
      if (r.id?.id) {
        llmModelId = r.id.id;
      } else if (response.data?.id?.id) {
        llmModelId = response.data.id.id;
      } else if (response.data?.Id?.id) {
        llmModelId = response.data.Id.id;
      }

      if (llmModelId) {
        return {
          success: true,
          data: {
            LlmModelID: llmModelId,
            ...model,
            IsActive: model.IsActive ?? true,
          } as LlmModel,
        };
      }
    }

    // If we couldn't extract the ID but still got success, return what we have
    if (response.success && !response.data?.LlmModelID) {
      return {
        success: true,
        data: {
          LlmModelID: 0, // Fallback - will be updated from actual data
          ...model,
          IsActive: model.IsActive ?? true,
        } as LlmModel,
      };
    }

    return response;
  }

  /**
   * Update existing LLM model
   */
  async update(id: number, model: any): Promise<ApiResponse<LlmModel>> {
    const webRequest = {
      Data: {
        LlmModelID: id,
        ...model,
      },
    };
    return this.put<LlmModel>(`/api/v1/ai-llm/models/${id}`, webRequest);
  }

  /**
   * Delete LLM model
   */
  async deleteById(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      ID: {
        ID: id,
      },
    };
    return this.delete<void>(`/api/v1/ai-llm/models/${id}`, webRequest);
  }

  /**
   * Enable LLM model
   */
  async enable(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      LlmModelID: {
        ID: id,
      },
    };
    return this.post<void>('/api/v1/ai-llm/models/enable', webRequest);
  }

  /**
   * Disable LLM model
   */
  async disable(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      LlmModelID: {
        ID: id,
      },
    };
    return this.post<void>('/api/v1/ai-llm/models/disable', webRequest);
  }
}

export const llmModelApiClient = new LlmModelApiClient();
